import { Identifier, UserInfo } from '@types';
import { config } from '../../../config';
import React, { useMemo, useState } from 'react';
import { selectLoan, selectUserInfo } from '@store/selectors/widgets.selectors';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { withHeaderFooter } from '../../HOCs/withHeaderFooter/withHeaderFooter';
import { PurchaseNavigationProps } from '../../data';
import { AuthenticatedLeadPage, i18n } from '@fmc-digital/marketing';
import { Container } from '@mui/material';
import { I18nextProvider } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { instance } from '@utils/fetch';
import { ErrorMessage, getSteps, SuccessMessage } from '@utils/auth-lead-form';
import { useSnackbar } from 'notistack';
import { logUserEvent } from '@analytics/amplifyLogging';
import { useSnackBarProps } from '@hooks/useSnackbarProps';
import { removeHVtileChildProduct, removeTileOffer } from '@store/actions/widgets.actions';
import { activityEventUpdate } from 'fmc-digital-common';
import { useWidgetsQueryClient } from '@hooks/useWidgetsQueryClient';
import { useVWOContext } from 'context/VWOContext';
import { allowedVWOActionsMapping } from '@constants';

export const PurchaseForm = withHeaderFooter(
    () => {
        const { RecordVWOEvent } = useVWOContext();
        const navigate = useNavigate();
        const params = useParams();
        const { state: routeState } = useLocation();
        const userInfo: UserInfo = useSelector(selectUserInfo);
        const loanList: Identifier[] = useSelector(selectLoan);
        const selectedLoan = loanList.find(loanList => loanList.identifier === params.loanNumber);
        const { address1, address2, city, state, zip } = selectedLoan.propertyAddress;
        const { email, firstName, lastName, mobile } = userInfo;

        const steps = getSteps('PU');
        const [applicationId, setApplicationId] = useState('');
        const successSnackBar = useSnackBarProps({ severity: 'success' });
        const errorSnackBar = useSnackBarProps({ severity: 'error' });

        const { enqueueSnackbar } = useSnackbar();
        const dispatch = useDispatch();
        const { updateCache } = useWidgetsQueryClient();

        const mutation = useMutation({
            mutationFn: ({ values, type }: any) => {
                return instance.request({
                    method: type === 'update' && applicationId ? 'put' : 'post',
                    url: applicationId ? `${config.env('leadSubmissionUrl')}/${applicationId}` : config.env('leadSubmissionUrl'),
                    data: values
                });
            },
            onSuccess: ({ data }, variables) => {
                setApplicationId(data.id);
                if (variables.type === 'submit') {
                    logUserEvent({
                        productCode: routeState?.offerType || 'NA',
                        loanType: routeState?.portCode || 'NA',
                        type: 'PurchaseAuthenticatedLeadForm',
                        action: 'PurchaseLeadSubmitSuccess'
                    });
                    const url = config.env('activityUpdateUrl');
                    activityEventUpdate(
                        [
                            {
                                loanNumber: selectedLoan.identifier,
                                activityType: routeState.offerType,
                                activityResult: 'SUBMITTED'
                            }
                        ],
                        url
                    );
                    const offerId = {
                        loanNumber: selectedLoan.identifier,
                        product: routeState.offerType,
                        offerGUID: routeState?.offerGUID
                    };
                    updateCache(offerId);
                    if (routeState.offerType.indexOf('HV') === 0) dispatch(removeHVtileChildProduct(offerId));
                    else dispatch(removeTileOffer(offerId));
                    enqueueSnackbar(SuccessMessage('AuthPurchase'), successSnackBar);
                    navigate('/');
                    const vwoEvent = allowedVWOActionsMapping['PurchaseLeadSubmitSuccess'];
                    RecordVWOEvent(vwoEvent, { product: routeState?.offerType });
                    return;
                }
                logUserEvent({
                    productCode: routeState?.offerType || 'NA',
                    loanType: routeState?.portCode || 'NA',
                    type: 'PurchaseAuthenticatedLeadForm',
                    action: 'PurchaseLeadUpdateSuccess'
                });
                const vwoEvent = allowedVWOActionsMapping['PurchaseLeadUpdateSuccess'];
                RecordVWOEvent(vwoEvent, { product: routeState?.offerType });
            },
            onError: (_, variables) => {
                if (variables.type === 'submit') {
                    logUserEvent({
                        productCode: routeState?.offerType || 'NA',
                        loanType: routeState?.portCode || 'NA',
                        type: 'PurchaseAuthenticatedLeadForm',
                        action: 'PurchaseLeadSubmitFailed'
                    });
                    enqueueSnackbar(ErrorMessage('AuthPurchase'), errorSnackBar);
                    return;
                }
                logUserEvent({
                    productCode: routeState?.offerType || 'NA',
                    loanType: routeState?.portCode || 'NA',
                    type: 'PurchaseAuthenticatedLeadForm',
                    action: 'PurchaseLeadUpdateFailed'
                });
            }
        });
        const defaultValues = useMemo(
            () => ({
                offerGUID: routeState?.offerGUID,
                leadOriginationSource: routeState?.leadOriginationSource,
                product: routeState?.offerType,
                borrowers: [
                    {
                        address: {
                            street: address1,
                            street2: address2,
                            city: city,
                            state: state,
                            zipCode: zip
                        },
                        email: email,
                        firstName: firstName,
                        lastName: lastName,
                        middleName: '',
                        nameSuffix: '',
                        phone: mobile
                    }
                ],
                loanNumber: params.loanNumber,
                productInfo: {
                    type: 'purchase',
                    purchaseDetails: {
                        type: 'PU',
                        puDetails: {
                            timeFrame: '',
                            purchasePrice: 0,
                            homeType: undefined,
                            usage: undefined,
                            zipCode: '',
                            state: ''
                        }
                    }
                },
                referralSource: 'ACP',
                utmDetails: {
                    utmDevice: 'Web',
                    utmDevicemodel: 'Chrome'
                }
            }),
            []
        );
        React.useEffect(() => {
            logUserEvent({
                productCode: routeState?.offerType || 'NA',
                loanType: routeState?.portCode || 'NA',
                type: 'PurchaseAuthenticatedLeadForm',
                action: 'SubmittingPurchaseInitialLead'
            });
            const vwoEvent = allowedVWOActionsMapping['SubmittingPurchaseInitialLead'];
            RecordVWOEvent(vwoEvent, { product: routeState?.offerType });
            mutation.mutate({ type: 'init', values: defaultValues });
        }, []);
        return (
            <I18nextProvider i18n={i18n}>
                <Container maxWidth="md">
                    <AuthenticatedLeadPage
                        returnToDashboard={() => navigate('../')}
                        tollFreeNo="8334481927"
                        steps={steps}
                        defaultValues={defaultValues}
                        handleSubmitOrUpdate={(type, values) => {
                            if (!mutation.isLoading) mutation.mutate({ type, values });
                        }}
                    />
                </Container>
            </I18nextProvider>
        );
    },
    { ...PurchaseNavigationProps }
);
